<template>
  <v-layout row fill-height justify-center>
    <v-dialog v-model="editorDialog" fullscreen transition="dialog-bottom-transition" :overlay="false">
      <v-card>
        <v-toolbar color="secondary white--text">
          <v-toolbar-title class="subheading ttu">{{
              messages.label.ADI_XML
          }}</v-toolbar-title>
          <v-list class="secondary ml-3">
            <v-list-tile class="white--text">
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Title</v-list-tile-sub-title>
                <v-list-tile-title>{{ selectedAsset.title }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Version</v-list-tile-sub-title>
                <v-list-tile-title>{{
                    selectedAsset.metadataVersion
                }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Provider</v-list-tile-sub-title>
                <v-list-tile-title>{{
                    selectedAsset.provider
                }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">State</v-list-tile-sub-title>
                <v-list-tile-title>{{ selectedAsset.state }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn @click.stop="refreshSearchResult" flat>
              <v-icon class="white--text">close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <v-layout class="py-2">
            <v-flex md8>
              <div class="dt">
                <v-icon color="error darken-2" class="dtc v-mid" v-if="selectedAsset.state === 'ERROR'">error</v-icon>
                <v-icon color="accent darken-2" class="dtc v-mid" v-else>info</v-icon>
                <span class="subheading dtc v-mid pl-2">{{ messages.label.STATE_INFORMATION }} ({{
                    selectedAsset.state
                }}):
                  <span class="caption">{{ selectedAsset.stateInfo }}</span>
                </span>
              </div>
            </v-flex>
            <v-flex md4 v-show="!readOnly && !hasBillingId" class="text-xs-right">
              <v-layout>
                <v-flex xs12 class="text-lg-right" v-show="!showBillingIdFilters">
                  <v-btn color="primary" flat @click="displayBillingIdFilters()">
                    <v-icon left>add_circle</v-icon>
                    <span>ASSIGN BILLING ID</span>
                  </v-btn>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex xs6 v-show="showBillingIdFilters" class="reduce_padding">
                  <v-select v-model="selectedAffiliate" :items="affiliateList" dense item-text="name" item-value="name"
                    required label="Affiliate"></v-select>
                </v-flex>
                <v-flex xs3 v-show="showBillingIdFilters">
                  <v-btn color="primary" class="increase_margin" :loading="updatingBillingId"
                    :disabled="updatingBillingId" @click="assignBillingId()">
                    {{ messages.label.ASSIGN }}
                  </v-btn>
                </v-flex>
                <v-flex xs3 v-show="showBillingIdFilters">
                  <v-btn color="grey lighten-1 white--text" class="top_margin"
                    @click.stop="showBillingIdFilters = false">
                    {{ messages.label.CANCEL }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <!-- Read Only View -->
          <div v-show="readOnly">
            <v-card :height="editorHeight">
              <v-card-text>
                <div ref="editorReadOnly" class="vop--ace-editor"></div>
              </v-card-text>
            </v-card>
            <v-card>
              <v-card-actions>
                <v-btn color="primary" @click="editorDialog = false">
                  {{ messages.label.CLOSE }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
          <!-- Editor View -->
          <v-stepper v-model="editingStep" v-show="!readOnly">
            <v-stepper-header>
              <v-stepper-step step="1" :complete="editingStep > 1" :editable="stepsEditable">
                {{ messages.label.EDIT }}
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="2" :complete="editingStep > 2" :editable="stepsEditable">
                {{ messages.label.REVIEW }}
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="3" v-bind:rules="[() => !didSubmitFail]">
                <span v-if="didSubmitFail">
                  {{ messages.label.ERROR }}
                </span>
                <span v-else>
                  {{ messages.label.COMPLETE }}
                </span>
              </v-stepper-step>
            </v-stepper-header>
            <v-stepper-items>
              <!-- STEP 1 -->
              <v-stepper-content step="1">
                <v-progress-linear v-if="updatingBillingId" v-bind:indeterminate="true"></v-progress-linear>
                <!-- Validation -->
                <v-alert color="error" icon="warning" value="true" v-if="manifestValidationError != ''">
                  <span v-html="manifestValidationError"></span>
                </v-alert>
                <v-card :height="editorHeight">
                  <div ref="editorEditable" class="vop--ace-editor"></div>
                </v-card>
                <v-card>
                  <v-btn color="primary" v-bind:disabled="!canPreview" @click="displayDifferences">
                    {{ messages.label.CONTINUE }}
                  </v-btn>
                  <v-btn color="grey lighten-1 white--text" @click.stop="editorDialog = false">
                    {{ messages.label.CANCEL }}
                  </v-btn>
                </v-card>
              </v-stepper-content>
              <!-- STEP 2 -->
              <v-stepper-content step="2">
                <v-progress-linear v-if="submittingXML" v-bind:indeterminate="true"></v-progress-linear>
                <v-card :height="editorHeight">
                  <div class="vop--acediff-editor-container">
                    <div>
                      <div class="vop--acediff-editor" ref="diffEditorLeft"></div>
                    </div>
                    <div class="vop--acediff-gutter" ref="diffGutter"></div>
                    <div>
                      <div class="vop--acediff-editor" ref="diffEditorRight"></div>
                    </div>
                  </div>
                </v-card>
                <v-card>
                  <v-btn color="primary" :loading="submittingXML" :disabled="submittingXML" @click="submitXML">
                    {{ messages.label.SUBMIT }}
                  </v-btn>
                  <v-btn color="grey lighten-1 white--text" @click.stop="editorDialog = false">
                    {{ messages.label.CANCEL }}
                  </v-btn>
                </v-card>
              </v-stepper-content>
              <!-- STEP 3 -->
              <v-stepper-content step="3">
                <v-card>
                  <v-alert color="success" icon="check_circle" value="true" v-if="xmlSubmitSuccess">
                    <span v-html="xmlSubmitResponseMsg"></span>
                  </v-alert>
                  <v-alert color="error" icon="warning" value="true" v-else>
                    <span v-html="xmlSubmitResponseMsg"></span>
                  </v-alert>
                </v-card>
                <v-card>
                  <v-btn color="primary" v-if="xmlSubmitSuccess" @click="refreshSearchResult">
                    {{ messages.label.DONE }}
                  </v-btn>
                  <v-btn color="primary" v-else @click="refreshSearchResult">
                    {{ messages.label.CLOSE }}
                  </v-btn>
                </v-card>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
          <v-snackbar :timeout="snackbar.timeout" :top="true" :color="snackbar.color" v-model="snackbar.show">
            {{ snackbar.message }}
            <v-btn flat icon @click.native="snackbar.show = false">
              <v-icon>cancel</v-icon>
            </v-btn>
          </v-snackbar>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import ace from "brace"
import AceDiff from "brace-diff"
import "brace/mode/xml"
import "brace/theme/eclipse"
import "brace/ext/searchbox"
import PreIngestService from "@/PreIngest/PreIngestService"
import * as preIngestDataService from "@/PreIngest/PreIngestDataService"
import {
  VOD_SYCN_PREINGEST_DATA_SERVICE_URL,
  VOD_SYCN_OFFERING_WEB_SERVICE_URL,
  VOD_SYCN_PREINGEST_WEB_SERVICE_URL
} from "@/store/constants/getter-types"
import messages from "@/PreIngest/messages.json"

export default {
  name: "PreIngestMetadataEdit",
  data() {
    return {
      editorDialog: false,
      assetMetadata: {},
      originalXML: "",
      canPreview: false,
      editingStep: 0,
      stepsEditable: false,
      isXMLStale: true,
      submittingXML: false,
      updatingBillingId: false,
      xmlSubmitSuccess: false,
      loadXMLErrorMsg: "",
      modifiedXML: "",
      selectedAsset: {},
      xmlSubmitResponseMsg: "",
      readOnly: false,
      hasBillingId: false,
      showBillingIdFilters: false,
      affiliateList: this.getAffiliates(),
      selectedAffiliate: this.getAffiliates()[0].name,
      snackbar: {
        show: false,
        color: "",
        message: "",
        timeout: 20000
      },
      retrievedBillingId: "",
      manifestValidationError: ""
    }
  },
  computed: {
    ...mapState(["mpxParentAccount"]),
    ...mapGetters([
      VOD_SYCN_PREINGEST_DATA_SERVICE_URL,
      VOD_SYCN_OFFERING_WEB_SERVICE_URL,
      VOD_SYCN_PREINGEST_WEB_SERVICE_URL
    ]),
    editorHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "65vh"
        case "sm":
          return "65vh"
        case "md":
          return "68vh"
        case "lg":
          return "68vh"
        case "xl":
          return "80vh"
      }
    },
    didSubmitFail() {
      return this.editingStep === 3 && !this.xmlSubmitSuccess
    }
  },
  methods: {
    /**
     * Check if XML is in valid state, if not disable 'CONTINUE' button
     */
    validateXML(event, editor) {
      let xmlSyntaxError = false
      let annotationLists = editor.getSession().getAnnotations()
      for (var i in annotationLists) {
        let annotationError = annotationLists[i].type
        if (annotationError === "error" || annotationError === "warning") {
          xmlSyntaxError = true
          break
        }
      }
      if (xmlSyntaxError) {
        this.canPreview = false
      } else {
        this.canPreview = true
      }
    },

    /**
     * Check if XML has billing id tag, if not display "Assign Billing ID" details
     */
    checkBillingId(assetObj) {
      let xmlString = assetObj.metadataXml
      let parser = new DOMParser()
      let xmlDocument = parser.parseFromString(xmlString, "text/xml")
      let billingId = xmlDocument.querySelector('[Name="Billing_ID"]')
      if (billingId) {
        let billingIdValue = billingId.getAttribute("Value")
        if (billingIdValue !== null) {
          this.hasBillingId = true
        }
      } else {
        this.hasBillingId = false
      }
    },

    /**
     * Make API call to get the "Billing Id" for selected ADI
     */
    assignBillingId() {
      this.updatingBillingId = true
      let reqBody = {
        assignBillingId: {
          affiliate: this.selectedAffiliate,
          assetReference: this.selectedAsset.paid
        }
      }
      preIngestDataService
        .assignBillingId(reqBody, this.VOD_SYCN_OFFERING_WEB_SERVICE_URL)
        .then(response => {
          this.insertBillingId(response.data.assignBillingIdResponse)
        })
        .catch(() => {
          this.updatingBillingId = false
          this.displayErrorSnackbar()
        })
    },

    /**
     * Insert the billing id tag into the ADI and update the ADI
     */
    insertBillingId(billingIdValue) {
      this.retrievedBillingId = billingIdValue
      let xmlString = this.selectedAsset.metadataXml
      let parser = new DOMParser()
      let xmlDocument = parser.parseFromString(xmlString, "text/xml")
      let titleMetadataRef = xmlDocument.querySelector('[Asset_Class="title"]')
      if (titleMetadataRef !== null) {
        let billingIdElement = xmlDocument.createElement("App_Data")
        billingIdElement.setAttribute("App", "MOD")
        billingIdElement.setAttribute("Name", "Billing_ID")
        billingIdElement.setAttribute("Value", billingIdValue)
        titleMetadataRef.parentElement.appendChild(billingIdElement)
        let serializer = new XMLSerializer()
        let updatedXMLString = serializer.serializeToString(xmlDocument)
        this.modifiedXML = updatedXMLString
        this.selectedAsset.metadataXml = updatedXMLString
        this.submitXML(this.hasBillingId)
      } else {
        this.updatingBillingId = false
        this.displayErrorSnackbar()
      }
    },

    /**
     * Open XML Editor dialog
     */
    openXMLEditor(assetObj, readOnly) {
      this.checkBillingId(assetObj)
      let editor
      this.readOnly = readOnly
      this.selectedAsset = assetObj
      this.originalXML = this.selectedAsset.metadataXml
      this.editorDialog = true
      this.editingStep = 1
      this.stepsEditable = false
      if (this.readOnly) {
        editor = ace.edit(this.$refs.editorReadOnly)
        editor.setReadOnly(true)
      } else {
        editor = ace.edit(this.$refs.editorEditable)
      }
      editor.setTheme("ace/theme/eclipse")
      editor.getSession().setMode("ace/mode/xml")
      editor.session.setValue(this.originalXML)
      editor.getSession().on("changeAnnotation", event => {
        this.validateXML(event, editor)
      })
    },

    /**
     * Display differences between original and modified version
     */
    displayDifferences() {
      let isValid = this.validateManifest()
      if (isValid) {
        this.manifestValidationError = "" // Reset manifest validation error
        let editor = ace.edit(this.$refs.editorEditable)
        this.editingStep = 2
        this.stepsEditable = true
        this.modifiedXML = editor.getValue()
        let aceDiffer = new AceDiff({
          mode: "ace/mode/xml",
          showDiffs: true,
          showConnectors: true,
          gutterEl: this.$refs.diffGutter,
          left: {
            element: this.$refs.diffEditorLeft,
            theme: "ace/theme/eclipse",
            editable: false,
            copyLinkEnabled: false
          },
          right: {
            element: this.$refs.diffEditorRight,
            theme: "ace/theme/eclipse",
            editable: false,
            copyLinkEnabled: false
          }
        })
        let editors = aceDiffer.getEditors()
        // Set Value to the editors
        editors.left.setValue(this.originalXML, 1)
        editors.right.setValue(this.modifiedXML, 1)
      } else {
        // Show Error
        console.log("Manifest error", this.manifestValidationError)
      }
    },

    /**
     * Validate MANIFEST file
     */
    validateManifest() {
      // Only validate MANIFEST files
      if (this.selectedAsset.format !== "MANIFEST") {
        return true
      }
      let editor = ace.edit(this.$refs.editorEditable)
      let xmlString = editor.getValue()
      let parser = new DOMParser()
      let xmlDocument = parser.parseFromString(xmlString, "text/xml")
      let titleRef = xmlDocument.querySelector("title")
      let manifestIDRef = xmlDocument.querySelector("ManifestID")
      let isValid = true

      if (
        this.$lodash.isNull(titleRef) ||
        this.$lodash.isEmpty(titleRef.innerHTML.trim())
      ) {
        this.manifestValidationError = messages.error.MANIFEST_VALIDATION_TITLE
        isValid = false
      } else if (
        this.$lodash.isNull(manifestIDRef) ||
        this.$lodash.isEmpty(manifestIDRef.innerHTML.trim())
      ) {
        this.manifestValidationError =
          messages.error.MANIFEST_VALIDATION_MANIFEST_ID
        isValid = false
      }

      return isValid
    },

    /**
     * Submit XML Changes
     */
    submitXML(hasBillingId) {
      if (!hasBillingId) {
        this.updatingBillingId = true
      } else {
        this.submittingXML = true
      }
      let reqBody = {
        updateMetadata: {
          updateMetadataRequest: {
            manifestId: this.selectedAsset.manifestId,
            metadataVersion: this.selectedAsset.metadataVersion,
            metadataXml: this.modifiedXML,
            processingRecordId: this.selectedAsset.id,
            sdHdLink: this.selectedAsset.sdHdLink
          }
        }
      }

      let account = this.selectedAsset.ownerId

      preIngestDataService
        .updateMetadata(
          reqBody,
          account,
          this.VOD_SYCN_PREINGEST_WEB_SERVICE_URL
        )
        .then(() => {
          if (!hasBillingId) {
            this.retrieveUpdatedRecord(this.selectedAsset)
            this.updatingBillingId = false
            this.displaySuccessSnackbar()
          } else {
            //  let updateMetadataResponse = response.data.updateMetadataResponse
            this.submittingXML = false
            this.editingStep = 3
            this.xmlSubmitSuccess = true
            this.stepsEditable = false
            this.xmlSubmitResponseMsg = this.getXMLSubmitSuccessMsg()
          }
        })
        .catch(error => {
          if (!hasBillingId) {
            this.updatingBillingId = false
            this.displayErrorSnackbar()
          } else {
            let response = error.response.data
            this.submittingXML = false
            this.editingStep = 3
            this.xmlSubmitSuccess = false
            this.xmlSubmitResponseMsg = this.getXMLSubmitFailureMsg(response)
            this.stepsEditable = true
          }
        })
    },

    /**
     * retriving the updated ADI for selected ADI after updating with BillingID
     */
    retrieveUpdatedRecord(assetObj) {
      let recordId = this.$lodash.last(assetObj.id.split("/"))
      let queryObject = {
        byId: recordId
      }
      preIngestDataService
        .retrieveProcessingRecords(
          queryObject,
          this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL
        )
        .then(response => {
          this.openXMLEditor(response.data, this.readOnly)
        })
        .catch(() => {
          this.displayErrorSnackbar()
        })
    },

    /**
     * getAffiliates - description
     *
     * @return {type}  description
     */
    getAffiliates() {
      return [
        {
          id: 1,
          name: "Frontier"
        }
      ]
    },

    /**
     * Construct XML submit success message
     */
    getXMLSubmitSuccessMsg() {
      let successMsg = messages.info.SUBMIT_XML_SUCCESS
      return successMsg
    },

    /**
     * Construct XML submit failure message
     */
    getXMLSubmitFailureMsg(response) {
      let correlationId = response.correlationId
      let description = response.description
      let failureMsg = messages.error.SUBMIT_XML_FAILURE
      failureMsg += "<br/>" + messages.label.REASON + description
      failureMsg += "<br/>" + messages.label.CORRELATION_ID + correlationId
      return failureMsg
    },

    /**
     * Construct snackbar success message
     */
    displaySuccessSnackbar() {
      let successMessage = this.messages.info.ASSIGN_BILLING_ID_SUCCESS.replace(
        "billingIdValue",
        this.retrievedBillingId
      )
      successMessage = successMessage.replace(
        "packageIdValue",
        this.selectedAsset.paid
      )
      this.snackbar.message = successMessage
      this.snackbar.show = true
      this.snackbar.color = "success"
    },

    /**
     * Construct snackbar failure message
     */
    displayErrorSnackbar() {
      this.snackbar.message = this.messages.error.ASSIGN_BILLING_ID_FAILURE
      this.snackbar.show = true
      this.snackbar.color = "error"
    },

    /**
     * Reload search results in the background
     */
    refreshSearchResult() {
      this.editorDialog = false
      if (this.xmlSubmitSuccess) {
        PreIngestService.$emit("event:refreshSearchResult")
      }
    },

    /**
     *
     * Display all the billing id filters for assigning
     */
    displayBillingIdFilters() {
      this.showBillingIdFilters = true
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    PreIngestService.$on("event:showMetadataEdit", (assetObj, readOnly) => {
      this.showBillingIdFilters = false
      this.hasBillingId = false
      this.openXMLEditor(assetObj, readOnly)
    })
  }
}
</script>

<style>
.vop--ace-editor {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.vop--ace-editor .ace_search_field,
.ace_searchbtn:last-child {
  color: black !important;
}

.vop--acediff-editor-container {
  display: flex;
  display: -webkit-flex;
  flex-direction: row;
  position: absolute;
  bottom: 0;
  width: 100%;
  top: 0px !important;
  left: 0px;
  height: 100%;
  overflow: auto;
}

.vop--acediff-editor-container>div {
  flex-grow: 1;
  -webkit-flex-grow: 1;
  position: relative;
}

.vop--acediff-editor-container>div.vop--acediff-gutter {
  flex: 0 0 60px;
  -webkit-flex: 0 0 60px;
  border-left: 1px solid #999999;
  border-right: 1px solid #999999;
  background-color: #efefef;
  overflow: hidden;
}

.vop--acediff-gutter svg {
  background-color: #efefef;
}

.vop--acediff-editor {
  position: absolute !important;
  top: 0;
  bottom: 0;
  width: 100%;
}

/** acediff specific styles, need to move in to a commmon style sheet */

.acediff-diff {
  background-color: #d8f2ff;
  border-top: 1px solid #a2d7f2;
  border-bottom: 1px solid #a2d7f2;
  position: absolute;
  z-index: 4;
}

.acediff-diff.targetOnly {
  height: 0px !important;
  border-top: 1px solid #a2d7f2;
  border-bottom: 0px;
  position: absolute;
}

.acediff-connector {
  fill: #d8f2ff;
  stroke: #a2d7f2;
}

.reduce_padding .input-group__details {
  min-height: 0px;
}

button.increase_margin {
  margin-top: 13px;
  margin-left: 17px;
}

button.top_margin {
  margin-top: 13px;
}
</style>



// WEBPACK FOOTER //
// src/PreIngest/ProcessingRecords/ProcessingRecordMetadataEdit.vue